import { ClaimResult, ExternalClaimStatus } from "./return-enums";

export interface ReportedClaimStatusRef {
  externalClaimStatus?: ExternalClaimStatus | null;
}

/** Under Review substatuses decide early: Submit to Close rejects and Submit
 * to Pay - Approved approves; the rest wait for a final main status. */
export const externalClaimStatusToClaimResult: Record<
  ExternalClaimStatus,
  ClaimResult
> = {
  [ExternalClaimStatus.NEW]: ClaimResult.PENDING,
  [ExternalClaimStatus.UNDER_REVIEW]: ClaimResult.PENDING,
  [ExternalClaimStatus.PENDING_DOCS]: ClaimResult.PENDING,
  [ExternalClaimStatus.PAID]: ClaimResult.APPROVED,
  [ExternalClaimStatus.CLOSED]: ClaimResult.REJECTED,
  [ExternalClaimStatus.CANCELLED]: ClaimResult.REJECTED,
  [ExternalClaimStatus.EXPIRED]: ClaimResult.REJECTED,
  [ExternalClaimStatus.PENDING_PREMIUMS]: ClaimResult.PENDING,
  [ExternalClaimStatus.SUBMIT_TO_PAY]: ClaimResult.PENDING,
  [ExternalClaimStatus.SUBMIT_TO_PAY_APPROVED]: ClaimResult.APPROVED,
  [ExternalClaimStatus.SUBMIT_TO_PAY_NOT_APPROVED]: ClaimResult.PENDING,
  [ExternalClaimStatus.SUBMIT_TO_CLOSE]: ClaimResult.REJECTED,
};

export function reportedClaimResult(
  claim: ReportedClaimStatusRef,
): ClaimResult {
  return claim.externalClaimStatus
    ? externalClaimStatusToClaimResult[claim.externalClaimStatus]
    : ClaimResult.PENDING;
}

/**
 * The insurer's decision across every reported claim: rejected only when all
 * are rejected, approved only when all are approved, otherwise still pending.
 * Callers that can see an empty list must handle it themselves; `every` on
 * nothing counts as all rejected, which the automation relies on.
 */
export function resolveReportedClaimsResult(
  reportedClaims: readonly ReportedClaimStatusRef[],
): ClaimResult {
  const results = reportedClaims.map(reportedClaimResult);
  if (results.every((result) => result === ClaimResult.REJECTED)) {
    return ClaimResult.REJECTED;
  }
  if (results.every((result) => result === ClaimResult.APPROVED)) {
    return ClaimResult.APPROVED;
  }
  return ClaimResult.PENDING;
}

/**
 * A claim the merchant processed before the insurer decided pays the merchant
 * only once the insurer approves; until then, and after a rejection, it doesn't.
 */
export function isMerchantPayoutAwaitingInsurer(return_: {
  processedBeforeInsurerDecision?: boolean | null;
  reportedClaims: readonly ReportedClaimStatusRef[];
}): boolean {
  return (
    !!return_.processedBeforeInsurerDecision &&
    resolveReportedClaimsResult(return_.reportedClaims) !== ClaimResult.APPROVED
  );
}
